import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { GridIcon, ListIcon, SearchIcon, FilterIcon, HeartIcon, UsersIcon, PaletteIcon, BriefcaseIcon, StoreIcon, MapPinIcon, StarIcon, ClockIcon, ImageIcon } from 'lucide-react';
import { useDarkMode } from '../../context/DarkModeContext';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import BackButton from '../../components/UI/BackButton';
import SaveToFavorites from '../../components/UI/SaveToFavorites';
// Types
type ViewMode = 'grid' | 'list';
type Category = 'all' | 'creative' | 'services' | 'vendors' | 'gallery';
interface FavoriteItem {
  id: string;
  name: string;
  type: 'creative' | 'service' | 'vendor' | 'gallery';
  role: string;
  specialty?: string;
  image: string;
  profileUrl: string;
  location?: string;
  rating?: number;
  reviewCount?: number;
  tags: string[];
  note?: string;
  dateSaved: string;
  creator?: string;
}
// Mock Data
const mockFavorites: FavoriteItem[] = [{
  id: 'c1',
  name: 'Elena V.',
  type: 'creative',
  role: 'Cover Artist',
  specialty: 'Fantasy & Sci-Fi',
  image: 'https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=400&q=80',
  profileUrl: '/creative/designers/elena-v',
  location: 'London, UK',
  rating: 4.9,
  reviewCount: 42,
  tags: ['Photomanipulation', 'Typography', 'Series Branding'],
  note: 'Great style for the upcoming trilogy covers.',
  dateSaved: '2024-02-10'
}, {
  id: 'c2',
  name: 'Marcus Chen',
  type: 'creative',
  role: 'Illustrator',
  specialty: 'Character Art',
  image: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=400&q=80',
  profileUrl: '/creative/illustrators/marcus-chen',
  location: 'Toronto, Canada',
  rating: 5.0,
  reviewCount: 28,
  tags: ['Anime Style', 'Semi-Realistic', 'NSFW Available'],
  dateSaved: '2024-01-15'
},
// Gallery items — saved while scrolling through galleries
{
  id: 'g1',
  name: 'Dark Enchantress Cover',
  type: 'gallery',
  role: 'Cover Art',
  specialty: 'Dark Fantasy',
  image: 'https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=400&q=80',
  profileUrl: '/creative/cover-gallery',
  tags: ['Fantasy', 'Dark', 'Illustrated'],
  creator: 'Elena V.',
  note: 'Love this moody style for Book 3.',
  dateSaved: '2024-02-18'
}, {
  id: 'g2',
  name: 'Warrior Princess Character Sheet',
  type: 'gallery',
  role: 'Character Art',
  specialty: 'Fantasy Characters',
  image: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=400&q=80',
  profileUrl: '/creative/character-art-gallery',
  tags: ['Character Design', 'Fantasy', 'Full Body'],
  creator: 'Marcus Chen',
  dateSaved: '2024-02-12'
}, {
  id: 'g3',
  name: 'Moody Author Portrait',
  type: 'gallery',
  role: 'Photography',
  specialty: 'Author Headshots',
  image: 'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=400&q=80',
  profileUrl: '/creative/photo-gallery',
  tags: ['Portrait', 'Moody', 'Professional'],
  creator: 'Lens & Light Studio',
  note: 'This vibe for new author photos.',
  dateSaved: '2024-01-28'
}, {
  id: 'g4',
  name: 'Celestial Book Box Design',
  type: 'gallery',
  role: 'Merch Design',
  specialty: 'Packaging',
  image: 'https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?w=400&q=80',
  profileUrl: '/creative/merch-gallery',
  tags: ['Packaging', 'Celestial', 'Gold Foil'],
  creator: 'Starlight Designs',
  dateSaved: '2024-02-05'
}, {
  id: 'g5',
  name: 'Ornate Chapter Header',
  type: 'gallery',
  role: 'Interior Art',
  specialty: 'Book Interiors',
  image: 'https://images.unsplash.com/photo-1516979187457-637abb4f9353?w=400&q=80',
  profileUrl: '/creative/interior-art-gallery',
  tags: ['Ornamental', 'Vintage', 'Chapter Dividers'],
  creator: 'Inkwell Illustrations',
  note: 'Perfect for the special edition interior.',
  dateSaved: '2024-01-20'
}, {
  id: 's1',
  name: 'Sarah Miller',
  type: 'service',
  role: 'Editor',
  specialty: 'Developmental Editing',
  image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&q=80',
  profileUrl: '/services/editing/sarah-miller',
  location: 'New York, USA',
  rating: 4.8,
  reviewCount: 156,
  tags: ['Romance', 'Thriller', 'Fast Turnaround'],
  note: 'Recommended by Jane for dev edits.',
  dateSaved: '2024-02-20'
}, {
  id: 's2',
  name: 'ProFormat Services',
  type: 'service',
  role: 'Formatting',
  specialty: 'Ebook & Print',
  image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=400&q=80',
  profileUrl: '/services/production/proformat',
  rating: 4.7,
  reviewCount: 89,
  tags: ['Vellum', 'InDesign', 'Custom Interiors'],
  dateSaved: '2023-12-05'
}, {
  id: 'v1',
  name: 'Midnight Press',
  type: 'vendor',
  role: 'Indie Publisher',
  specialty: 'Dark Romance',
  image: 'https://images.unsplash.com/photo-1478720568477-152d9b164e63?w=400&q=80',
  profileUrl: '/vendor/midnight-press',
  location: 'Seattle, WA',
  tags: ['Anthologies', 'Co-Marketing', 'Events'],
  note: 'Reach out for Halloween anthology collaboration.',
  dateSaved: '2024-01-30'
}, {
  id: 'v2',
  name: 'Bookish Box Co.',
  type: 'vendor',
  role: 'Merch Creator',
  specialty: 'Subscription Boxes',
  image: 'https://images.unsplash.com/photo-1549465220-1a8b9238cd48?w=400&q=80',
  profileUrl: '/vendor/bookish-box-co',
  location: 'Austin, TX',
  rating: 4.6,
  reviewCount: 210,
  tags: ['Wholesale', 'Custom Items', 'Candles'],
  dateSaved: '2024-02-25'
}];
const VendorFavorites = () => {
  const {
    darkMode
  } = useDarkMode();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [activeCategory, setActiveCategory] = useState<Category>('all');
  const [searchQuery, setSearchQuery] = useState('');
  // Filter items
  const filteredItems = mockFavorites.filter(item => {
    const matchesCategory = activeCategory === 'all' || activeCategory === 'creative' && item.type === 'creative' || activeCategory === 'services' && item.type === 'service' || activeCategory === 'vendors' && item.type === 'vendor' || activeCategory === 'gallery' && item.type === 'gallery';
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase()) || item.role.toLowerCase().includes(searchQuery.toLowerCase()) || item.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase())) || item.note && item.note.toLowerCase().includes(searchQuery.toLowerCase()) || item.creator && item.creator.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });
  // Helper to get category label
  const getCategoryLabel = (type: string) => {
    switch (type) {
      case 'creative':
        return 'Creative';
      case 'service':
        return 'Service';
      case 'vendor':
        return 'Vendor';
      case 'gallery':
        return 'Gallery';
      default:
        return type;
    }
  };
  // Helper to get category color
  const getCategoryColor = (type: string) => {
    switch (type) {
      case 'creative':
        return 'bg-pink-600 text-white dark:bg-pink-600 dark:text-white';
      case 'service':
        return 'bg-blue-600 text-white dark:bg-blue-600 dark:text-white';
      case 'vendor':
        return 'bg-purple-600 text-white dark:bg-purple-600 dark:text-white';
      case 'gallery':
        return 'bg-amber-600 text-white dark:bg-amber-600 dark:text-white';
      default:
        return 'bg-gray-600 text-white dark:bg-gray-600 dark:text-white';
    }
  };
  // Helper to get gallery badge color based on which section it was saved from
  const getGalleryRoleBadgeColor = (role: string) => {
    const r = role.toLowerCase();
    if (r.includes('cover')) return 'bg-indigo-600 text-white dark:bg-indigo-600 dark:text-white';
    if (r.includes('character')) return 'bg-rose-600 text-white dark:bg-rose-600 dark:text-white';
    if (r.includes('photo')) return 'bg-teal-600 text-white dark:bg-teal-600 dark:text-white';
    if (r.includes('merch') || r.includes('branding')) return 'bg-orange-600 text-white dark:bg-orange-600 dark:text-white';
    if (r.includes('interior')) return 'bg-emerald-600 text-white dark:bg-emerald-600 dark:text-white';
    // Fallback for any other gallery role
    return 'bg-amber-600 text-white dark:bg-amber-600 dark:text-white';
  };
  // Helper to get solid tag color for gallery overlay (high contrast on any image)
  const getGalleryTagColor = (role: string) => {
    const r = role.toLowerCase();
    if (r.includes('cover')) return 'bg-indigo-600 text-white';
    if (r.includes('character')) return 'bg-rose-600 text-white';
    if (r.includes('photo')) return 'bg-teal-600 text-white';
    if (r.includes('merch') || r.includes('branding')) return 'bg-orange-600 text-white';
    if (r.includes('interior')) return 'bg-emerald-600 text-white';
    return 'bg-amber-600 text-white';
  };
  return <div className={`min-h-screen py-8 ${dk.page}`} data-id="element-22703" data-mp="bdxmyn1t8nxj">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-22704" data-mp="bdeb9o2r6vry">
        <div className="mb-6" data-id="element-22705" data-mp="6et7xfs7fvza">
          <BackButton data-id="element-22706" data-mp="6fw0ds56e6hb" />
        </div>

        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4" data-id="element-22707" data-mp="1f8aj9sonvoo">
          <div data-id="element-22708" data-mp="3tir22k6qess9">
            <h1 className={`text-3xl font-bold mb-2 flex items-center gap-3 ${dk.textHeading}`} data-id="element-22709" data-mp="3tj96i43jsdb9">
              <HeartIcon className={`h-8 w-8 fill-current ${rt.textPrimary}`} data-id="element-22710" data-mp="2m44ficz4sbv0" />
              My Favorites
            </h1>
            <p className={`max-w-2xl ${dk.textSecondary}`} data-id="element-22711" data-mp="1j95dlc98dpbx">
              Your curated collection of creative professionals, service
              providers, and potential collaborators.
            </p>
          </div>
          <div className="flex items-center gap-3" data-id="element-22712" data-mp="1szhv0vlr12du">
            <div className="relative" data-id="element-22713" data-mp="1t1b56kvkm4ql">
              <SearchIcon className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${dk.textFaint}`} data-id="element-22714" data-mp="1o7zsi342utqr" />
              <input type="text" placeholder="Search favorites..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className={`pl-9 pr-4 py-2 rounded-lg border text-sm w-full md:w-64 focus:outline-none focus:ring-2 ${rt.inputFocus} ${dk.input}`} data-id="element-22715" data-mp="22vulzlpwt24w" />
            </div>
            <div className={`flex items-center gap-1 p-1 rounded-lg border ${dk.card}`} data-id="element-22716" data-mp="hl49hxegwz0z">
              <button onClick={() => setViewMode('list')} className={`p-2 rounded-md transition-colors ${viewMode === 'list' ? `${rt.bgMedium} ${rt.textPrimary}` : dk.textFaint}`} title="List View" data-id="element-22717" data-mp="cmj8vwvb0dc0">
                <ListIcon className="h-5 w-5" data-id="element-22718" data-mp="2fgjjjfvw3ngk" />
              </button>
              <button onClick={() => setViewMode('grid')} className={`p-2 rounded-md transition-colors ${viewMode === 'grid' ? `${rt.bgMedium} ${rt.textPrimary}` : dk.textFaint}`} title="Grid View" data-id="element-22719" data-mp="2fe70t0ib592g">
                <GridIcon className="h-5 w-5" data-id="element-22720" data-mp="2aii02tujxm4k" />
              </button>
            </div>
          </div>
        </div>

        {/* Category Tabs */}
        <div className={`border-b mb-8 ${dk.border}`} data-id="element-22721" data-mp="20ir1oh2vemz7">
          <nav className="-mb-px flex space-x-8 overflow-x-auto scrollbar-hide" data-id="element-22722" data-mp="20j9826j8yhku">
            {[{
            id: 'all',
            label: 'All Favorites',
            icon: null
          }, {
            id: 'creative',
            label: 'Creative Artists',
            icon: PaletteIcon
          }, {
            id: 'services',
            label: 'Services',
            icon: BriefcaseIcon
          }, {
            id: 'vendors',
            label: 'Vendors & Authors',
            icon: StoreIcon
          }, {
            id: 'gallery',
            label: 'Gallery Saves',
            icon: ImageIcon,
            count: mockFavorites.filter(i => i.type === 'gallery').length
          }].map(({
            id,
            label,
            icon: Icon,
            count
          }) => <button key={id} onClick={() => setActiveCategory(id as Category)} className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap flex items-center gap-2 ${activeCategory === id ? `${rt.borderAccent} ${rt.textPrimary}` : `border-transparent ${dk.textMuted}`}`} data-id="element-22723" data-mp="1hk5s8wivhgc6">
                {Icon && <Icon className="h-4 w-4" data-id="element-22724" data-mp="2cosl7r5pory4" />}
                {label}
                {count !== undefined && count > 0 && <span className={`text-xs px-1.5 py-0.5 rounded-full ${activeCategory === id ? rt.badge : dk.badgeNeutral}`} data-id="element-22725" data-mp="2ho5i74qarfef">
                    {count}
                  </span>}
              </button>)}
          </nav>
        </div>

        {/* Content */}
        {filteredItems.length > 0 ? viewMode === 'grid' ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-id="element-22726" data-mp="30ityltjl86wc">
              {filteredItems.map(item => item.type === 'gallery' ? <div key={item.id} className={`group relative rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-200 cursor-pointer border ${dk.card}`} data-id="element-22727" data-mp="3ad2lo1pun7n4">
                    <Link to={item.profileUrl} className="block" data-id="element-22728" data-mp="35ofp3u7vrl0q">
                      <div className="relative" data-id="element-22729" data-mp="35ml1271bbcsf">
                        <img src={item.image} alt={item.name} className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-105" data-id="element-22730" data-mp="31maryli8469c" />
                        <div className="absolute top-3 left-3 z-10" data-id="element-22731" data-mp="2mq38xoijxavn">
                          <span className={`px-2 py-1 text-xs font-bold uppercase rounded-full shadow-sm ${getGalleryRoleBadgeColor(item.role)}`} data-id="element-22732" data-mp="2mqd0vhn5ps3z">
                            {item.role}
                          </span>
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4" data-id="element-22733" data-mp="2rr97w42t1udm">
                          <h3 className="text-white font-semibold text-lg leading-tight" data-id="element-22734" data-mp="1owk5z7ro58py">
                            {item.name}
                          </h3>
                          {item.creator && <p className="text-gray-200 text-sm mt-0.5" data-id="element-22735" data-mp="1fvvuoktntmgr">
                              by {item.creator}
                            </p>}
                          {item.specialty && <span className="mt-2 inline-block bg-indigo-600/80 text-white text-xs px-2 py-1 rounded self-start" data-id="element-22736" data-mp="28kb6yzlepqoe">
                              {item.specialty}
                            </span>}
                          {item.note && <p className="text-gray-300 text-xs italic mt-2 line-clamp-2" data-id="element-22737" data-mp="23tb6xlgzqsho">
                              "{item.note}"
                            </p>}
                          {item.tags.length > 0 && <div className="flex flex-wrap gap-1 mt-2" data-id="element-22738" data-mp="2wbgix3o87ytf">
                              {item.tags.slice(0, 3).map((tag, idx) => <span key={idx} className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${getGalleryTagColor(item.role)}`} data-id="element-22739" data-mp="316hwfk4oxn5q" data-mpi={idx}>
                                  {tag}
                                </span>)}
                            </div>}
                        </div>
                      </div>
                    </Link>
                    <div className="absolute top-3 right-3 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-22740" data-mp="2dgxd304mknvx">
                      <SaveToFavorites itemId={item.id} initialSaved={true} data-id="element-22741" data-mp="28jouiyfkehu5" />
                    </div>
                  </div> : <div key={item.id} className={`group relative rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-200 border ${dk.card}`} data-id="element-22742" data-mp="1zhntdqh7nbn7">
                    <Link to={item.profileUrl} className="block" data-id="element-22743" data-mp="j0gqumll7lns">
                      <div className="aspect-[4/3] overflow-hidden relative" data-id="element-22744" data-mp="o0biisd6sf9w">
                        <img src={item.image} alt={item.name} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" data-id="element-22745" data-mp="ny8idfibl5rg" />
                        <div className="absolute top-3 left-3" data-id="element-22746" data-mp="bs9gambbygr">
                          <span className={`px-2 py-1 text-xs font-bold uppercase rounded-full shadow-sm ${getCategoryColor(item.type)}`} data-id="element-22747" data-mp="5a3kups9ah13">
                            {getCategoryLabel(item.type)}
                          </span>
                        </div>
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex flex-col justify-end p-4" data-id="element-22748" data-mp="3dp3p5o4v0asm">
                          <div className="text-white" data-id="element-22749" data-mp="2745pylh17ymm">
                            <p className="text-sm font-medium mb-1" data-id="element-22750" data-mp="224jdwfcxwofe">
                              Specialty
                            </p>
                            <p className="text-xs text-gray-200 mb-3" data-id="element-22751" data-mp="2g0mtb2j5ms7t">
                              {item.specialty || item.role}
                            </p>
                            {item.location && <div className="flex items-center text-xs text-gray-300" data-id="element-22752" data-mp="2l23rgqa980kp">
                                <MapPinIcon className="h-3 w-3 mr-1" data-id="element-22753" data-mp="2l09f40d1tgs8" />
                                {item.location}
                              </div>}
                          </div>
                        </div>
                      </div>
                      <div className="p-4" data-id="element-22754" data-mp="38s6kb8lztakd">
                        <div className="flex justify-between items-start mb-2" data-id="element-22755" data-mp="33stx2mr7m1h9">
                          <div data-id="element-22756" data-mp="33qqw57oiqnxr">
                            <h3 className={`font-bold text-lg truncate ${dk.textPrimary}`} data-id="element-22757" data-mp="3ne3ughd8qm8x">
                              {item.name}
                            </h3>
                            <p className={`text-sm ${rt.textPrimary}`} data-id="element-22758" data-mp="3dfeb0k5p4tqk">
                              {item.role}
                            </p>
                          </div>
                        </div>
                        {item.rating && <div className="flex items-center mb-3 text-sm" data-id="element-22759" data-mp="39em3pwgdwywx">
                            <StarIcon className="h-4 w-4 text-amber-400 fill-current mr-1" data-id="element-22760" data-mp="39gp1x3roxycq" />
                            <span className={`font-medium ${dk.textPrimary}`} data-id="element-22761" data-mp="2urj0d95drw1o">
                              {item.rating}
                            </span>
                            <span className={`ml-1 ${dk.textMuted}`} data-id="element-22762" data-mp="2lors1i7sxge9">
                              ({item.reviewCount})
                            </span>
                          </div>}
                        <div className="flex flex-wrap gap-1 mb-3" data-id="element-22763" data-mp="1fwudxwmbvgyw">
                          {item.tags.slice(0, 3).map((tag, idx) => <span key={idx} className={`text-xs px-2 py-0.5 rounded-full font-medium ${rt.badge}`} data-id="element-22764" data-mp="1kuceayma4nva" data-mpi={idx}>
                              {tag}
                            </span>)}
                        </div>
                        {item.note && <div className={`text-xs p-2 rounded-md italic mb-1 ${dk.surfaceMuted} ${dk.textMuted}`} data-id="element-22765" data-mp="23momw33j0ldv">
                            "{item.note}"
                          </div>}
                        {item.creator && <p className={`text-xs mt-1 ${dk.textFaint}`} data-id="element-22766" data-mp="2hkv5kbzwmesy">
                            by {item.creator}
                          </p>}
                      </div>
                    </Link>
                    <div className="absolute top-3 right-3 z-10" data-id="element-22767" data-mp="5zvym4bl1nbz">
                      <SaveToFavorites itemId={item.id} initialSaved={true} data-id="element-22768" data-mp="5xjk61519v3o" />
                    </div>
                  </div>)}
            </div> : <div className="space-y-4" data-id="element-22769" data-mp="fq7f0qcgdozr">
              {filteredItems.map(item => <div key={item.id} className={`flex flex-col sm:flex-row gap-4 p-4 rounded-xl shadow-sm border transition-all duration-200 hover:shadow-md ${dk.card}`} data-id="element-22770" data-mp="3o5r4rfdx0pa2">
                  <Link to={item.profileUrl} className="flex-shrink-0 w-full sm:w-32 h-32 rounded-lg overflow-hidden" data-id="element-22771" data-mp="1vy0nwxzuiki">
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" data-id="element-22772" data-mp="1dbmo0pf5tjlu" />
                  </Link>
                  <div className="flex-1 min-w-0 flex flex-col" data-id="element-22773" data-mp="1fh1g2olbols4">
                    <div className="flex justify-between items-start" data-id="element-22774" data-mp="1kfcpiy7pgu9t">
                      <div data-id="element-22775" data-mp="1pfqnfnflenu4">
                        <div className="flex items-center gap-2 mb-1" data-id="element-22776" data-mp="1pgikz3sr2vn1">
                          <span className={`px-2 py-0.5 text-xs font-bold uppercase rounded-full ${item.type === 'gallery' ? getGalleryRoleBadgeColor(item.role) : getCategoryColor(item.type)}`} data-id="element-22777" data-mp="1ueacvxteahr8">
                            {item.type === 'gallery' ? item.role : getCategoryLabel(item.type)}
                          </span>
                          {item.rating && <div className="flex items-center text-xs" data-id="element-22778" data-mp="e0bowf7qjnww">
                              <StarIcon className="h-3 w-3 text-amber-400 fill-current mr-1" data-id="element-22779" data-mp="e02210hzlwvf" />
                              <span className={dk.textSecondary} data-id="element-22780" data-mp="ixkc4u7qqi50">
                                {item.rating} ({item.reviewCount})
                              </span>
                            </div>}
                        </div>
                        <Link to={item.profileUrl} data-id="element-22781" data-mp="2n87120oswvm0">
                          <h3 className={`text-xl font-bold transition-colors hover:${rt.textPrimary} ${dk.textPrimary}`} data-id="element-22782" data-mp="2nb2xtlowxe8v">
                            {item.name}
                          </h3>
                        </Link>
                        <p className={`text-sm font-medium ${rt.textPrimary}`} data-id="element-22783" data-mp="28iixest5dkv4">
                          {item.role}
                          {item.specialty ? ` • ${item.specialty}` : ''}
                          {item.creator ? ` • by ${item.creator}` : ''}
                        </p>
                      </div>
                      <SaveToFavorites itemId={item.id} initialSaved={true} data-id="element-22784" data-mp="1umf7hbpb3yog" />
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2" data-id="element-22785" data-mp="1pnkgvnl3l5ts">
                      {item.tags.map((tag, idx) => <span key={idx} className={`text-xs px-2 py-1 rounded-md font-medium ${rt.badge}`} data-id="element-22786" data-mp="3umasn885nl8a" data-mpi={idx}>
                          {tag}
                        </span>)}
                    </div>
                    <div className="mt-auto pt-3 flex items-center justify-between" data-id="element-22787" data-mp="36x9ed4cn5fv3">
                      <div className={`flex items-center gap-4 text-xs ${dk.textMuted}`} data-id="element-22788" data-mp="36zcdcdvrujby">
                        {item.location && <div className="flex items-center" data-id="element-22789" data-mp="2x0wn0d4kje3i">
                            <MapPinIcon className="h-3 w-3 mr-1" data-id="element-22790" data-mp="31yy3qreiv1bt" />
                            {item.location}
                          </div>}
                        <div className="flex items-center" data-id="element-22791" data-mp="1069w85uuhrq8">
                          <ClockIcon className="h-3 w-3 mr-1" data-id="element-22792" data-mp="105qotj2tmlqg" />
                          Saved on {item.dateSaved}
                        </div>
                      </div>
                      {item.note && <div className={`text-xs italic px-2 py-1 rounded ${dk.surfaceMuted} ${dk.textMuted}`} data-id="element-22793" data-mp="2fnepe8e2e8m3">
                          Note: {item.note}
                        </div>}
                    </div>
                  </div>
                </div>)}
            </div> : <div className="text-center py-16" data-id="element-22794" data-mp="2zd78r42sya25">
            <div className={`inline-flex p-6 rounded-full mb-6 ${dk.surfaceMuted}`} data-id="element-22795" data-mp="3ddrofrdf7prf">
              <HeartIcon className={`h-12 w-12 ${dk.textFaint}`} data-id="element-22796" data-mp="38dvi3lh93m8p" />
            </div>
            <h3 className={`text-xl font-bold mb-2 ${dk.textHeading}`} data-id="element-22797" data-mp="1iyp2u4rhsrts">
              No favorites yet
            </h3>
            <p className={`max-w-md mx-auto mb-8 ${dk.textSecondary}`} data-id="element-22798" data-mp="1e15pd72mcj2w">
              Start building your rolodex! Browse our directories and click the
              heart icon to save professionals you'd like to work with.
            </p>
            <div className="flex flex-wrap justify-center gap-4" data-id="element-22799" data-mp="3j3kmpyosjly4">
              <Link to="/creative/all" className={`px-6 py-3 rounded-lg font-medium text-white transition-colors ${rt.primaryButton}`} data-id="element-22800" data-mp="3n2a8aejzg9su">
                Browse Creatives
              </Link>
              <Link to="/services/all" className={`px-6 py-3 rounded-lg border font-medium transition-colors ${dk.card}`} data-id="element-22801" data-mp="u0vdhr99yju">
                Find Services
              </Link>
              <Link to="/vendors/directory" className={`px-6 py-3 rounded-lg border font-medium transition-colors ${dk.card}`} data-id="element-22802" data-mp="en0b9kuy9urx">
                Vendor Directory
              </Link>
            </div>
          </div>}
      </div>
    </div>;
};
export default VendorFavorites;